<template>
  <div class="game-settings-container">
    <h1 class="game-title">{{ $t('PvPTFgame.PvPTFgameTitle') }}</h1>
    <div class="setting-section">
      <div class="setting-title">{{ $t('PvPTFgame.topicLabel') }}</div>
      <div class="vocabulary-counter">
        {{ topic.length }}/20
      </div>
      <input class="input-text" type="text" v-model="topic" :placeholder="$t('PvPTFgame.inputTopic')" maxlength="20">
    </div>

    <div class="setting-section">
      <div class="setting-title">{{ $t('PvPTFgame.questionLabel') }}</div>
      <div class="vocabulary-counter">
        {{ questionList.length }}/10
      </div>
      
      <div v-for="(question, index) in questionList" :key="index" class="question-item">
        <div class="question-content">
          <div class="question-text">{{ question.text }}</div>
          <div class="question-answer" :class="{ 'true': question.isTrue, 'false': !question.isTrue }">
            <svg v-if="question.isTrue" class="icon-svg correct" viewBox="0 0 341.333 341.333">
              <path d="M170.667,0C76.41,0,0,76.41,0,170.667s76.41,170.667,170.667,170.667s170.667-76.41,170.667-170.667S264.923,0,170.667,0z
                M170.667,298.667c-70.692,0-128-57.308-128-128s57.308-128,128-128s128,57.308,128,128S241.359,298.667,170.667,298.667z"/>
            </svg>
            <svg v-else class="icon-svg wrong" viewBox="0 0 1024 1024">
              <path d="M195.2 195.2a64 64 0 0 1 90.496 0L512 421.504 738.304 195.2a64 64 0 0 1 90.496 90.496L602.496 512 828.8 738.304a64 64 0 0 1-90.496 90.496L512 602.496 285.696 828.8a64 64 0 0 1-90.496-90.496L421.504 512 195.2 285.696a64 64 0 0 1 0-90.496z"/>
            </svg>
          </div>
        </div>
        <button class="icon-button delete" @click="removeQuestion(index)">
          <svg class="icon-svg" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a1 1 0 01-1 1H3a1 1 0 110-2h14a1 1 0 011 1z"/>
          </svg>
        </button>
      </div>

      <div class="question-input">
        <input class="input-text" type="text" v-model="questionInput" :placeholder="$t('PvPTFgame.inputQuestion')">
        <div class="answer-selector">
          <div 
            class="answer-option" 
            :class="{ 'selected': newQuestionIsTrue }"
            @click="newQuestionIsTrue = true"
          >
            <svg class="icon-svg" viewBox="0 0 341.333 341.333">
              <path d="M170.667,0C76.41,0,0,76.41,0,170.667s76.41,170.667,170.667,170.667s170.667-76.41,170.667-170.667S264.923,0,170.667,0z
                M170.667,298.667c-70.692,0-128-57.308-128-128s57.308-128,128-128s128,57.308,128,128S241.359,298.667,170.667,298.667z"/>
            </svg>
          </div>
          <div 
            class="answer-option" 
            :class="{ 'selected': !newQuestionIsTrue }"
            @click="newQuestionIsTrue = false"
          >
            <svg class="icon-svg" viewBox="0 0 1024 1024">
              <path d="M195.2 195.2a64 64 0 0 1 90.496 0L512 421.504 738.304 195.2a64 64 0 0 1 90.496 90.496L602.496 512 828.8 738.304a64 64 0 0 1-90.496 90.496L512 602.496 285.696 828.8a64 64 0 0 1-90.496-90.496L421.504 512 195.2 285.696a64 64 0 0 1 0-90.496z"/>
            </svg>
          </div>
        </div>
        <button class="add-button" @click="addQuestion" :disabled="!questionInput.trim()">
          <svg class="icon-svg" viewBox="0 0 24 24">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M13 6C13 5.44771 12.5523 5 12 5C11.4477 5 11 5.44771 11 6V11H6C5.44771 11 5 11.4477 5 12C5 12.5523 5.44771 13 6 13H11V18C11 18.5523 11.4477 19 12 19C12.5523 19 13 18.5523 13 18V13H18C18.5523 13 19 12.5523 19 12C19 11.4477 18.5523 11 18 11H13V6Z" />
          </svg>
        </button>
      </div>
    </div>

    <div class="setting-section">
      <div class="setting-title">{{ $t('PvPTFgame.gameDuration') }}</div>
      <div class="duration-selector">
        <select v-model="gameDuration">
          <option v-for="option in durationOptions" :key="option" :value="option">
            {{ option }} {{ $t('PvPTFgame.second') }}
          </option>
        </select>
      </div>
    </div>

    <div class="setting-section">
      <div class="setting-title">{{ $t('PvPTFgame.answerMode') }}</div>
      <div class="answer-mode-selector">
        <label class="radio-option">
          <input type="radio" v-model="answerMode" value="sync" name="answerMode">
          <span class="radio-label">{{ $t('PvPTFgame.syncMode') }}</span>
        </label>
        <label class="radio-option">
          <input type="radio" v-model="answerMode" value="individual" name="answerMode">
          <span class="radio-label">{{ $t('PvPTFgame.individualMode') }}</span>
        </label>
      </div>
    </div>

    <div class="action-buttons">
      <button class="clear-button" @click="clearData">{{ $t('PvPTFgame.clearData') }}</button>
      <button class="save-button" @click="saveSettings" :disabled="!canSaveSettings">{{ $t('PvPTFgame.saveSettingsButton') }}</button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, inject, onMounted, getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();
const that = getCurrentInstance().proxy;

// 通信工具類
const MessageBridge = {
    // 環境類型
    ENV: {
        WEBVIEW: 'webview',
        WEB: 'web'
    },
    // 當前環境
    currentEnv: 'webview',
    // 初始化
    init() {
        this.currentEnv = window?.chrome?.webview ? this.ENV.WEBVIEW : this.ENV.WEB;
        return this;
    },
    // 發送消息
    postMessage(data, origin = '*') {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.postMessage(data);
        } else {
            window.postMessage(data, origin);
        }
    },
    // 添加消息監聽器
    addEventListener(callback) {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.addEventListener('message', event => {
                callback(event);
            });
        } else {
            window.addEventListener('message', event => {
                callback(event);
            });
        }
    }
};

// 初始化消息橋接器
const messageBridge = MessageBridge.init();

onMounted(() => {
  messageBridge.addEventListener(event => {
    const msg = event.data;
    console.log(msg, 'msg');
  });
  
  // 從 sessionStorage 讀取配置資料
  const savedConfig = sessionStorage.getItem('PvPTFGameConfig');
  if (savedConfig) {
    try {
      const config = JSON.parse(savedConfig);
      console.log('從 sessionStorage 讀取到的配置:', config);
      
      // 填充表單欄位
      if (config.topic) topic.value = config.topic;
      if (config.questionList && Array.isArray(config.questionList)) {
        questionList.value = [...config.questionList];
      }
      if (config.questionDuration) {
        // 找到最接近的時間選項
        const closest = durationOptions.reduce((prev, curr) => {
          return (Math.abs(curr - config.questionDuration) < Math.abs(prev - config.questionDuration)) ? curr : prev;
        });
        gameDuration.value = closest;
      }
      if (config.answerMode) {
        answerMode.value = config.answerMode;
      }
    } catch (error) {
      console.error('解析 sessionStorage 中的配置時出錯:', error);
    }
  }
});

const $Message = inject("$Message");

// 主題
const topic = ref('');

// 題目列表
const questionList = ref([]);
const questionInput = ref('');
const newQuestionIsTrue = ref(true);

// 遊戲時長選項
const durationOptions = [7, 10, 15];
const gameDuration = ref(10);

// 作答模式選項
const answerMode = ref('sync'); // 'sync' 或 'individual'




// 計算是否可以儲存設置
const canSaveSettings = computed(() => questionList.value.length > 0 && topic.value.trim() !== '');

// 新增題目
const addQuestion = () => {
  if (questionInput.value.trim() === '') {
    return;
  }

  if (questionList.value.length >= 10) {
    $Message.warning({
      content: that.$t('PvPTFgame.maxQuestions'),
      timeout: 2000,
    });
    return;
  }

  // 檢查是否有重複題目
  const isDuplicate = questionList.value.some(q => q.text === questionInput.value.trim());
  if (isDuplicate) {
    $Message.warning({
      content: that.$t('PvPTFgame.questionExists'),
      timeout: 2000,
    });
    return;
  }

  // 添加新題目
  questionList.value.push({
    text: questionInput.value.trim(),
    isTrue: newQuestionIsTrue.value
  });

  // 清空輸入框
  questionInput.value = '';
};

// 移除題目
const removeQuestion = (index) => {
  questionList.value.splice(index, 1);
};

// 清空設定數據
const clearData = () => {
  topic.value = '';
  questionList.value = [];
  questionInput.value = '';
  newQuestionIsTrue.value = true;
  gameDuration.value = 10;
  answerMode.value = 'sync';
  
  // 清除 sessionStorage 中的配置
  sessionStorage.removeItem('PvPTFGameConfig');
  
};

// 儲存設置
const saveSettings = () => {
  if (!canSaveSettings.value) {
    return;
  }

  const gameSettings = {
    topic: topic.value,
    questionList: JSON.parse(JSON.stringify(questionList.value)),
    questionDuration: gameDuration.value,
    answerMode: answerMode.value,
  };

  console.log('遊戲設定:', gameSettings);
  
  // 發送設定到原生應用
  messageBridge.postMessage({
    action: 'Save.TFGameSetting',
    data: gameSettings
  });
  
  // 導航到遊戲頁面
  router.push('/ht-pvp-tfgame-play');
};


</script>

<style scoped>
.game-settings-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.game-title {
    font-size: 2rem;
    text-align: center;
    margin-bottom: 30px;
  }

.setting-section {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  position: relative;
}

.setting-title {
  font-weight: bold;
  margin-bottom: 10px;
  font-size: 18px;
}

.vocabulary-counter {
  position: absolute;
  top: 15px;
  right: 15px;
  color: #666;
}

.input-text {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  margin-bottom: 10px;
}

.question-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: white;
  padding: 10px 15px;
  border-radius: 4px;
  margin-bottom: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.question-content {
  display: flex;
  align-items: center;
  flex: 1;
}

.question-text {
  flex: 1;
  margin-right: 10px;
}

.question-answer {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: bold;
  width: 30px;
  height: 30px;
}

.question-answer.true {
  background-color: #e6f7e6;
}

.question-answer.false {
  background-color: #f8d7da;
}

.icon-svg {
  width: 20px;
  height: 20px;
}

.icon-svg.correct {
  fill: #28a745;
}

.icon-svg.wrong {
  fill: #dc3545;
}

.icon-button {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.icon-button:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.icon-button.delete {
  margin-left: 10px;
}

.icon-button.delete .icon-svg {
  fill: #dc3545;
}

.question-input {
  display: flex;
  flex-direction: column;
  background-color: white;
  padding: 15px;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.answer-selector {
  display: flex;
  margin: 10px 0;
}

.answer-option {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 8px;
  border: 1px solid #ddd;
  cursor: pointer;
  transition: all 0.2s;
  height: 40px;
}

.answer-option:first-child {
  border-radius: 4px 0 0 4px;
}

.answer-option:last-child {
  border-radius: 0 4px 4px 0;
}

.answer-option .icon-svg {
  fill: #6c757d;
}

.answer-option.selected {
  background-color: #007bff;
  border-color: #007bff;
}

.answer-option.selected .icon-svg {
  fill: white;
}

.add-button {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.2s;
  height: 44px;
}

.add-button .icon-svg {
  fill: white;
  width: 24px;
  height: 24px;
}

.add-button:hover {
  background-color: #218838;
}

.add-button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.duration-selector select {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  background-color: white;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 30px;
}

.clear-button {
  background-color: #f44336;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 1.1rem;
  cursor: pointer;
  font-family: inherit;
  transition: background-color 0.2s;
}

.clear-button:hover {
  background-color: #da190b;
}

.save-button {
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 1.1rem;
  cursor: pointer;
  font-family: inherit;
  transition: background-color 0.2s;
}

.save-button:hover {
  background-color: #45a049;
}

.save-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

/* 作答模式選擇器樣式 */
.answer-mode-selector {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.radio-option {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.radio-option:hover {
  background-color: #f8f9fa;
}

.radio-option input[type="radio"] {
  margin-right: 8px;
}

.radio-label {
  font-size: 16px;
  user-select: none;
}


</style>